/**
 * GPU Providers - RunPod, Vast.ai, Lambda Labs, CoreWeave
 * Kullanici kendi GPU kiralama hesabini baglar
 * Local LLM'in cloud versiyonu - kendi GPU'n yoksa kirala
 */
import { ExternalLink, HardDrive, Key, Server, Zap } from 'lucide-react'
import { Link } from 'react-router-dom'

interface GpuProvider {
  id: string
  name: string
  tagline: string
  color: string
  pricing: string
  bestFor: string[]
  features: string[]
  setupUrl: string
  docsUrl: string
  apiType: string
}

const GPU_PROVIDERS: GpuProvider[] = [
  {
    id: 'runpod',
    name: 'RunPod',
    tagline: 'Cheapest GPU cloud · OpenAI-compatible endpoints',
    color: '#7c3aed',
    pricing: 'From $0.19/hr (RTX 3090)',
    bestFor: ['LLM inference', 'Fine-tuning', 'Stable Diffusion'],
    features: [
      'Serverless GPU (pay per second)',
      'Persistent pods (always-on)',
      'OpenAI-compatible API endpoint',
      'vLLM, Ollama, llama.cpp templates',
      'Community templates (1-click deploy)',
      '100+ GPU types (A100, H100, RTX 4090)',
    ],
    setupUrl: 'https://runpod.io',
    docsUrl: 'https://docs.runpod.io',
    apiType: 'openai_compat',
  },
  {
    id: 'vastai',
    name: 'Vast.ai',
    tagline: 'Peer-to-peer GPU marketplace · Lowest prices',
    color: '#0ea5e9',
    pricing: 'From $0.10/hr (RTX 3080)',
    bestFor: ['Budget inference', 'Training', 'Batch processing'],
    features: [
      'P2P GPU marketplace (community GPUs)',
      'Spot & on-demand instances',
      'Docker-based deployment',
      'SSH access to instances',
      'Jupyter notebook support',
      'Vast CLI for automation',
    ],
    setupUrl: 'https://vast.ai',
    docsUrl: 'https://vast.ai/docs',
    apiType: 'openai_compat',
  },
  {
    id: 'lambda',
    name: 'Lambda Labs',
    tagline: 'Research-grade GPUs · H100 clusters',
    color: '#f59e0b',
    pricing: 'From $0.50/hr (A10)',
    bestFor: ['Research', 'Large model training', 'Enterprise'],
    features: [
      'H100, A100, A10 instances',
      'On-demand & reserved instances',
      'Lambda Cloud API',
      'Jupyter + SSH access',
      'NVLink multi-GPU clusters',
      'Storage volumes (persistent)',
    ],
    setupUrl: 'https://lambdalabs.com',
    docsUrl: 'https://docs.lambdalabs.com',
    apiType: 'openai_compat',
  },
  {
    id: 'coreweave',
    name: 'CoreWeave',
    tagline: 'Enterprise Kubernetes GPU cloud',
    color: '#10b981',
    pricing: 'Custom pricing',
    bestFor: ['Enterprise', 'Kubernetes workloads', 'Large scale'],
    features: [
      'Kubernetes-native GPU cloud',
      'H100, A100, L40S clusters',
      'Persistent storage (NFS, S3)',
      'Managed inference endpoints',
      'VPC networking',
      'SLA guarantees',
    ],
    setupUrl: 'https://coreweave.com',
    docsUrl: 'https://docs.coreweave.com',
    apiType: 'openai_compat',
  },
]

const HOW_IT_WORKS = [
  { step: '1', title: 'GPU Kirala', desc: 'RunPod veya Vast.ai\'de hesap aç, GPU instance başlat' },
  { step: '2', title: 'Model Deploy Et', desc: 'vLLM veya Ollama template\'i seç, model yükle (1-click)' },
  { step: '3', title: 'Endpoint Al', desc: 'OpenAI-compatible endpoint URL\'ini kopyala' },
  { step: '4', title: 'Local LLM\'e Ekle', desc: 'RunAI\'de Local LLM → Add Endpoint → URL\'i yapıştır' },
]

export default function GpuProvidersPage() {
  return (
    <div>
      {/* Baslik */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '4px' }}>GPU Providers</h2>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
          Kendi GPU&apos;n yoksa kirala. RunPod, Vast.ai veya Lambda Labs&apos;te model deploy et,
          endpoint&apos;i Local LLM&apos;e ekle. Sanki kendi bilgisayarında çalışıyor gibi.
        </p>
      </div>

      {/* Nasil calisir */}
      <div style={{
        padding: '20px',
        background: 'rgba(59,130,246,0.06)',
        border: '1px solid rgba(59,130,246,0.2)',
        borderRadius: '12px',
        marginBottom: '24px',
      }}>
        <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '14px', color: 'var(--text-primary)' }}>
          Nasıl Çalışır?
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
          {HOW_IT_WORKS.map(item => (
            <div key={item.step} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
              <div style={{
                width: '24px', height: '24px', borderRadius: '50%',
                background: '#3b82f6', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '11px', fontWeight: 700, flexShrink: 0,
              }}>{item.step}</div>
              <div>
                <div style={{ fontSize: '12px', fontWeight: 600 }}>{item.title}</div>
                <div style={{ fontSize: '11px', color: 'var(--text-secondary)', lineHeight: 1.4 }}>{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: '14px', fontSize: '12px', color: 'var(--text-secondary)' }}>
          Endpoint ekledikten sonra{' '}
          <Link to="/aicloud/app/platforms/local-llm" style={{ color: '#3b82f6' }}>Local LLM sayfasına</Link>
          {' '}git ve &quot;Add Endpoint&quot; ile bağla.
        </div>
      </div>

      {/* Provider kartlari */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {GPU_PROVIDERS.map(p => (
          <div key={p.id} style={{
            padding: '24px',
            background: 'var(--card-bg)',
            border: '1px solid var(--border-color)',
            borderRadius: '12px',
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
              {/* Sol: logo + isim */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', minWidth: '200px' }}>
                <div style={{
                  width: '44px', height: '44px',
                  background: `${p.color}20`,
                  border: `1px solid ${p.color}40`,
                  borderRadius: '10px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <Server size={20} color={p.color} />
                </div>
                <div>
                  <div style={{ fontSize: '15px', fontWeight: 700 }}>{p.name}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{p.tagline}</div>
                </div>
              </div>

              {/* Sag: fiyat + best for */}
              <div style={{ flex: 1, minWidth: '200px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <Zap size={12} color={p.color} />
                  <span style={{ fontSize: '12px', fontWeight: 600, color: p.color }}>{p.pricing}</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
                  {p.bestFor.map(b => (
                    <span key={b} style={{
                      fontSize: '11px', padding: '2px 8px',
                      background: `${p.color}12`,
                      border: `1px solid ${p.color}30`,
                      borderRadius: '4px', color: p.color,
                    }}>{b}</span>
                  ))}
                </div>
                {/* Features */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '4px' }}>
                  {p.features.map(f => (
                    <div key={f} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                      <div style={{ width: '4px', height: '4px', borderRadius: '50%', background: p.color, flexShrink: 0 }} />
                      {f}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Butonlar */}
            <div style={{ marginTop: '16px', display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
              <a href={p.setupUrl} target="_blank" rel="noreferrer" style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '7px 14px',
                background: p.color,
                color: '#fff',
                borderRadius: '7px',
                textDecoration: 'none',
                fontSize: '12px', fontWeight: 600,
              }}>
                <ExternalLink size={12} /> Get Started
              </a>
              <a href={p.docsUrl} target="_blank" rel="noreferrer" style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '7px 14px',
                background: 'transparent',
                color: 'var(--text-secondary)',
                border: '1px solid var(--border-color)',
                borderRadius: '7px',
                textDecoration: 'none',
                fontSize: '12px',
              }}>
                Docs ↗
              </a>
              <Link to="/aicloud/app/platforms/local-llm" style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '7px 14px',
                background: 'transparent',
                color: '#3b82f6',
                border: '1px solid rgba(59,130,246,0.3)',
                borderRadius: '7px',
                textDecoration: 'none',
                fontSize: '12px',
              }}>
                <Key size={12} /> Add as Local LLM Endpoint
              </Link>
              <span style={{ fontSize: '11px', color: 'var(--text-tertiary)', marginLeft: '4px' }}>
                API type: {p.apiType}
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* HuggingFace notu */}
      <div style={{
        marginTop: '24px',
        padding: '16px 20px',
        background: 'rgba(255,193,7,0.06)',
        border: '1px solid rgba(255,193,7,0.2)',
        borderRadius: '10px',
        display: 'flex', gap: '12px', alignItems: 'flex-start',
      }}>
        <HardDrive size={16} color="#f59e0b" style={{ flexShrink: 0, marginTop: '2px' }} />
        <div>
          <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '4px' }}>Model İndirmek İçin: HuggingFace Hub</div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
            GPU provider&apos;ında model deploy ederken GGUF/GPTQ modellerini HuggingFace&apos;den indir.
            Popüler kaynaklar: <a href="https://huggingface.co/TheBloke" target="_blank" rel="noreferrer" style={{ color: '#3b82f6' }}>TheBloke</a>,{' '}
            <a href="https://huggingface.co/bartowski" target="_blank" rel="noreferrer" style={{ color: '#3b82f6' }}>bartowski</a>,{' '}
            <a href="https://huggingface.co/unsloth" target="_blank" rel="noreferrer" style={{ color: '#3b82f6' }}>unsloth</a>.
            Local LLM sayfasında da model önerileri var.
          </div>
        </div>
      </div>
    </div>
  )
}
